/**
 * @file lpSolve.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-08-25
 * @description Two-phase simplex. Minimise cᵀx subject to Ax {<=,=,>=} b, x >= 0.
 *
 *              A least-cost blend is a linear program and nothing less will do.
 *              Everything about the problem is linear in the inclusions — cost
 *              is Σ pᵢxᵢ/100, every nutrient is Σ nᵢₖxᵢ/100, the total is Σxᵢ —
 *              so a greedy "swap the dearest for the cheapest that still
 *              passes" walks into local minima that look solved and are not.
 *              Worse, it cannot tell "no blend satisfies this spec" from "I
 *              gave up", and those need different answers from the user.
 *
 *              Phase 1 minimises the sum of artificial variables. If it cannot
 *              reach zero the constraints genuinely contradict each other, and
 *              that is a proof rather than a guess. Phase 2 then minimises the
 *              real objective from the feasible vertex phase 1 found.
 *
 *              Bland's rule throughout — always the lowest-index candidate.
 *              It is slower than Dantzig's on paper and it cannot cycle, which
 *              matters here because degenerate vertices are the norm: a blend
 *              with several materials pinned at a bound hits them constantly.
 *              These problems are tens of variables, so the speed is free.
 */

const EPS = 1e-9;

/**
 * @param {number[]}   c    objective coefficients, length n
 * @param {number[][]} A    constraint matrix, m x n
 * @param {number[]}   b    right-hand sides, length m
 * @param {string[]}   ops  per row: '<=' | '=' | '>='
 * @param {number}     [maxIterations]
 * @returns {{status:'optimal'|'infeasible'|'unbounded'|'iteration-limit',
 *            x:number[], objective:number}}
 */
export function solveLP(c, A, b, ops, maxIterations = 5000) {
  const m = A.length;
  const n = c.length;
  if (!m) {
    // No constraints: every variable wants to be zero, or the thing is
    // unbounded below. Either way there is nothing to pivot on.
    return { status: c.some((v) => v < -EPS) ? 'unbounded' : 'optimal',
             x: new Array(n).fill(0), objective: 0 };
  }

  // Normalise to b >= 0. Negating a row flips its sense, which is why this
  // happens before slack and artificial columns are decided.
  const rows = [];
  for (let i = 0; i < m; i += 1) {
    let row = A[i].slice();
    let rhs = b[i];
    let op = ops[i];
    if (rhs < 0) {
      row = row.map((v) => -v);
      rhs = -rhs;
      op = op === '<=' ? '>=' : op === '>=' ? '<=' : '=';
    }
    rows.push({ row, rhs, op });
  }

  // Column layout: structural, then one slack/surplus per inequality, then one
  // artificial per row that needs a basis column of its own.
  const slackOf = new Array(m).fill(-1);
  const artificialOf = new Array(m).fill(-1);
  let width = n;
  for (let i = 0; i < m; i += 1) {
    if (rows[i].op !== '=') { slackOf[i] = width; width += 1; }
  }
  const firstArtificial = width;
  for (let i = 0; i < m; i += 1) {
    // A '<=' row's slack is already a unit column, so it needs no artificial.
    if (rows[i].op !== '<=') { artificialOf[i] = width; width += 1; }
  }
  const artificialCount = width - firstArtificial;

  // Tableau: m constraint rows, each `width` wide, plus an rhs column.
  const T = [];
  const basis = new Array(m);
  for (let i = 0; i < m; i += 1) {
    const t = new Array(width + 1).fill(0);
    for (let j = 0; j < n; j += 1) t[j] = rows[i].row[j];
    if (slackOf[i] >= 0) t[slackOf[i]] = rows[i].op === '<=' ? 1 : -1;
    if (artificialOf[i] >= 0) t[artificialOf[i]] = 1;
    t[width] = rows[i].rhs;
    T.push(t);
    basis[i] = artificialOf[i] >= 0 ? artificialOf[i] : slackOf[i];
  }

  let iterations = 0;

  /** One simplex run against the given cost vector. */
  const run = (cost, allowed) => {
    // Reduced costs: the objective row with the basic columns eliminated.
    const z = new Array(width + 1).fill(0);
    for (let j = 0; j < width; j += 1) z[j] = cost[j];
    for (let i = 0; i < m; i += 1) {
      const cb = cost[basis[i]];
      if (!cb) continue;
      for (let j = 0; j <= width; j += 1) z[j] -= cb * T[i][j];
    }

    for (;;) {
      if (iterations >= maxIterations) return 'iteration-limit';
      iterations += 1;

      // Bland: the lowest-index column that improves. Never the largest — that
      // is where cycling lives.
      let enter = -1;
      for (let j = 0; j < width; j += 1) {
        if (!allowed(j)) continue;
        if (z[j] < -EPS) { enter = j; break; }
      }
      if (enter === -1) return 'optimal';

      let leave = -1;
      let best = Infinity;
      for (let i = 0; i < m; i += 1) {
        const a = T[i][enter];
        if (a <= EPS) continue;
        const ratio = T[i][width] / a;
        // Bland again on the tie: smallest basis index, not first row seen.
        if (ratio < best - EPS
            || (leave >= 0 && Math.abs(ratio - best) <= EPS && basis[i] < basis[leave])) {
          best = ratio;
          leave = i;
        }
      }
      if (leave === -1) return 'unbounded';

      // Pivot.
      const pivot = T[leave][enter];
      for (let j = 0; j <= width; j += 1) T[leave][j] /= pivot;
      for (let i = 0; i < m; i += 1) {
        if (i === leave) continue;
        const factor = T[i][enter];
        if (!factor) continue;
        for (let j = 0; j <= width; j += 1) T[i][j] -= factor * T[leave][j];
      }
      const factor = z[enter];
      if (factor) for (let j = 0; j <= width; j += 1) z[j] -= factor * T[leave][j];
      basis[leave] = enter;
    }
  };

  // ── Phase 1: drive the artificials to zero ────────────────────────────────
  if (artificialCount > 0) {
    const phase1 = new Array(width).fill(0);
    for (let j = firstArtificial; j < width; j += 1) phase1[j] = 1;

    const status = run(phase1, () => true);
    if (status === 'iteration-limit') return { status, x: [], objective: 0 };

    let infeasibility = 0;
    for (let i = 0; i < m; i += 1) {
      if (basis[i] >= firstArtificial) infeasibility += T[i][width];
    }
    if (infeasibility > 1e-7) {
      return { status: 'infeasible', x: new Array(n).fill(0), objective: 0 };
    }

    // An artificial still in the basis sits at zero. Pivot it out on any real
    // column in its row; if the whole row is zero the constraint was redundant
    // and the row can stay where it is — it constrains nothing.
    for (let i = 0; i < m; i += 1) {
      if (basis[i] < firstArtificial) continue;
      let replacement = -1;
      for (let j = 0; j < firstArtificial; j += 1) {
        if (Math.abs(T[i][j]) > EPS) { replacement = j; break; }
      }
      if (replacement === -1) continue;
      const pivot = T[i][replacement];
      for (let j = 0; j <= width; j += 1) T[i][j] /= pivot;
      for (let k = 0; k < m; k += 1) {
        if (k === i) continue;
        const factor = T[k][replacement];
        if (!factor) continue;
        for (let j = 0; j <= width; j += 1) T[k][j] -= factor * T[i][j];
      }
      basis[i] = replacement;
    }
  }

  // ── Phase 2: the real objective, artificials barred from re-entering ──────
  const phase2 = new Array(width).fill(0);
  for (let j = 0; j < n; j += 1) phase2[j] = c[j];

  const status = run(phase2, (j) => j < firstArtificial);
  if (status !== 'optimal') return { status, x: new Array(n).fill(0), objective: 0 };

  const x = new Array(n).fill(0);
  for (let i = 0; i < m; i += 1) {
    if (basis[i] < n) x[basis[i]] = T[i][width];
  }
  // Round away the -1e-16 that a pivot leaves behind; a negative inclusion is
  // nonsense to show a user even at that scale.
  for (let j = 0; j < n; j += 1) if (Math.abs(x[j]) < 1e-9) x[j] = 0;

  const objective = x.reduce((sum, v, j) => sum + v * c[j], 0);
  return { status: 'optimal', x, objective };
}

export default solveLP;
